<template>
  <div :class="device.desktop ? `area pa-6` : `area pa-2`">
    <div class="area-panel">
      <!-- Header panel: judul, pencarian & aksi -->
      <div class="area-panel-header">
        <v-icon
          small
          color="#60a5fa"
          class="mr-2"
        >mdi-map-marker-radius-outline</v-icon>
        <span class="area-panel-title">DAFTAR AREA</span>
        <span class="area-panel-count">{{ records.length }} area</span>
        <v-spacer></v-spacer>
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Cari area..."
          single-line
          hide-details
          solo
          flat
          dense
          dark
          background-color="#0a1224"
          color="#60a5fa"
          class="area-search"
          style="max-width: 300px"
        ></v-text-field>
        <v-tooltip
          bottom
          color="#1e2c4f"
        >
          <template v-slot:activator="{ on }">
            <v-btn
              icon
              dark
              v-on="on"
              v-show="page.actions.refresh"
              @click="fetchRecords"
              class="ml-2"
            >
              <v-icon color="#94a3b8">mdi-refresh</v-icon>
            </v-btn>
          </template>
          <span>Refresh Data</span>
        </v-tooltip>
        <v-btn
          depressed
          dark
          small
          class="area-btn-add ml-2"
          v-show="page.actions.add"
          @click="openForm"
        >
          <v-icon
            left
            small
          >mdi-plus-circle-outline</v-icon>
          Tambah
        </v-btn>
      </div>

      <!-- Tabel desktop -->
      <v-data-table
        v-show="device.desktop"
        :headers="headers"
        :items="records"
        :items-per-page="table.options.itemsPerPage"
        :page.sync="table.options.page"
        class="area-table"
        dark
        :loading="loading.table"
        loading-text="Memuat data area..."
        :search="search"
        hide-default-footer
        @page-count="table.options.pageCount = $event"
        show-select
      >
        <v-progress-linear
          slot="progress"
          color="#3b82f6"
          height="2"
          indeterminate
        ></v-progress-linear>

        <template v-slot:item.name="{ value }">
          <div class="area-name">
            <v-icon
              small
              color="#60a5fa"
              class="mr-2"
            >mdi-map-marker-outline</v-icon>
            {{ value }}
          </div>
        </template>

        <template v-slot:item.status="{ value }">
          <v-chip
            :color="value.color"
            small
          >{{ value.text }}</v-chip>
        </template>

        <template v-slot:item.id="{ value }">
          <v-menu
            bottom
            origin="center center"
            transition="scale-transition"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                color="#60a5fa"
                v-bind="attrs"
                v-on="on"
              >
                mdi-dots-vertical-circle-outline
              </v-icon>
            </template>

            <v-list
              dark
              color="#101b36"
            >
              <v-list-item
                @click="editRecord(value)"
                v-show="page.actions.edit"
              >
                <v-list-item-title>
                  <v-icon
                    color="orange"
                    class="mr-1"
                  >mdi-pencil-circle</v-icon>Ubah Data
                </v-list-item-title>
              </v-list-item>
              <v-list-item
                @click="postDeleteRecord(value)"
                v-show="page.actions.delete"
              >
                <v-list-item-title>
                  <v-icon
                    class="mr-1"
                    color="red"
                  >mdi-delete-circle</v-icon>Hapus
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </v-data-table>

      <!-- Daftar mobile -->
      <v-list
        subheader
        three-line
        v-show="device.mobile"
        dark
        color="#101b36"
      >
        <v-list-item-group
          multiple
          active-class=""
        >
          <v-list-item
            v-for="item in filterItems"
            :key="item.id"
          >
            <template v-slot:default="{ active }">
              <v-list-item-action>
                <v-checkbox
                  :input-value="active"
                  color="#60a5fa"
                ></v-checkbox>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>{{ item.name }}</v-list-item-title>
                <v-list-item-subtitle>{{ item.description }} </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-menu
                  bottom
                  origin="center center"
                  transition="scale-transition"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      color="#60a5fa"
                      v-bind="attrs"
                      v-on="on"
                    >
                      mdi-dots-vertical-circle-outline
                    </v-icon>
                  </template>

                  <v-list
                    dark
                    color="#101b36"
                  >
                    <v-divider v-if="page.delete || page.edit"></v-divider>
                    <v-list-item
                      @click="editRecord(item.id)"
                      v-show="page.actions.edit"
                    >
                      <v-list-item-title>
                        <v-icon color="orange">mdi-pencil-circle</v-icon>
                        Ubah Data
                      </v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      @click="postDeleteRecord(item.id)"
                      v-show="page.actions.delete"
                    >
                      <v-list-item-title>
                        <v-icon color="red">mdi-delete-circle</v-icon>
                        Hapus Data
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </v-list-item-action>
            </template>
          </v-list-item>
        </v-list-item-group>
      </v-list>
    </div>

    <!-- Dialog form tambah/ubah -->
    <v-dialog
      transition="dialog-bottom-transition"
      v-model="form.add"
      :max-width="device.desktop ? `600px` : `100%`"
      persistent
      :fullscreen="device.mobile"
    >
      <v-card
        color="#101b36"
        dark
        class="area-dialog"
      >
        <div class="area-dialog-header">
          <v-icon
            small
            color="#60a5fa"
            class="mr-2"
          >{{ form.edit ? `mdi-pencil-circle-outline` : `mdi-plus-circle-outline` }}</v-icon>
          <span class="area-panel-title">{{ form.edit ? `UBAH DATA AREA` : `TAMBAH DATA AREA` }}</span>
        </div>
        <v-card-text class="mt-4">
          <v-col cols="12">
            <v-text-field
              outlined
              dark
              color="#60a5fa"
              background-color="#0a1224"
              label="Area"
              placeholder="Contoh: Kecamatan Tigaraksa"
              persistent-hint
              hint="Nama wilayah/kecamatan lokasi kamera CCTV"
              v-model="record.name"
              dense
              prepend-inner-icon="mdi-map-marker-outline"
            ></v-text-field>
          </v-col>
          <v-col cols="12">
            <v-textarea
              label="Keterangan"
              dark
              color="#60a5fa"
              background-color="#0a1224"
              v-model="record.description"
              outlined
              dense
              persistent-hint
              hint="Penjelasan singkat tentang area ini"
              rows="3"
              prepend-inner-icon="mdi-text-box-outline"
            ></v-textarea>
          </v-col>
        </v-card-text>
        <v-divider class="area-dialog-divider"></v-divider>
        <v-card-actions class="justify-end pa-4">
          <v-btn
            text
            color="#94a3b8"
            @click="closeForm"
          >Batal</v-btn>
          <v-btn
            depressed
            dark
            class="area-btn-add"
            v-show="!form.edit"
            @click="postAddNewRecord"
          >
            <v-icon
              left
              small
            >mdi-content-save-outline</v-icon>
            Tambah
          </v-btn>
          <v-btn
            depressed
            dark
            class="area-btn-add"
            v-show="form.edit"
            @click="postUpdateRecord"
          >
            <v-icon
              left
              small
            >mdi-content-save-outline</v-icon>
            Ubah
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import "animate.css";
import {
  LMap,
  LTileLayer,
  LMarker,
  LIcon,
  LPopup,
  LTooltip,
} from "vue2-leaflet";
import { OpenStreetMapProvider } from "leaflet-geosearch";
import VGeosearch from "vue2-leaflet-geosearch";
import "leaflet/dist/leaflet.css";

export default {
  name: "master-data-area",
  components: {
    LMap,
    LTileLayer,
    LMarker,
    VGeosearch,
    LIcon,
    LPopup,
    LTooltip,
  },
  data: () => ({
    num: 1,
    headers: [
      {
        text: "AREA",
        align: "start",
        sortable: false,
        value: "name",
      },
      {
        text: "KETERANGAN",
        value: "description",
        align: "start",
        sortable: false,
      },

      {
        text: "AKSI",
        value: "id",
        width: 100,
        sortable: false,
        align: "center",
      },
    ],
    search: null,
    path: null,
    filename: null,

    //map property
    url: "https://tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution:
      '&copy; <a target="_blank" href="http://osm.org/copyright">Dinas Perhubungan Kab. Tangerang</a>',
    zoom: 13,
    //-6.1716001, 106.6405384
    center: [-6.1716001, 106.6405384],

    geosearchOptions: {
      // Important part Here
      provider: new OpenStreetMapProvider(),
    },
    marker: {
      id: "m1",
      position: { lat: -6.1716001, lng: 106.6405384 },
      tooltip: "Pilih lokasi POS pantau",
      draggable: true,
      visible: true,
    },
  }),
  computed: {
    ...mapState([
      "page",
      "theme",
      "http",
      "device",
      "record",
      "records",
      "loading",
      "event",
      "snackbar",
      "form",
      "table",
    ]),
    filterItems() {
      if (this.search != null) {
        return this.records.filter((item) => {
          return (
            item.name.toLowerCase().indexOf(this.search.toLowerCase()) > -1 ||
            item.nip.toLowerCase().indexOf(this.search.toLowerCase()) > -1
          );
        });
      } else {
        return this.records;
      }
    },
  },
  created() {
    this.setPage({
      crud: true,
      dataUrl: "api/superadmin/master-data/areas",
      pagination: false,
      title: "MASTER AREA",
      subtitle: "Berikut Daftar Seluruh Area Yang Tersedia",
      showtable: true,
      breadcrumbs: [
        {
          text: "Data Master",
          disabled: true,
          href: "",
        },
        {
          text: "Area",
          disabled: false,
          href: "",
        },
      ],
      actions: {
        refresh: true,
        add: true,
        edit: true,
        delete: true,
        bulkdelete: false,
        print: false,
        export: false,
        help: false,
      },
    });
    this.fetchRecords();
  },
  mounted() {},
  methods: {
    ...mapActions([
      "setPage",
      "fetchRecords",
      "postAddNew",
      "postEdit",
      "postUpdate",
      "postConfirmDelete",
      "assignFileBrowse",
      "setLoading",
      "setRecord",
      "setForm",
    ]),
    openForm: function () {
      this.setForm({
        add: true,
        edit: false,
      });
      this.setRecord({});
      this.filename = null;
      setTimeout(function () {
        window.dispatchEvent(new Event("resize"));
      }, 250);
    },
    closeForm: function () {
      this.setForm({
        add: false,
        edit: false,
      });
    },
    postAddNewRecord: function () {
      this.postAddNew(this.record).then(() => {
        this.closeForm();
      });
    },
    editRecord: function (val) {
      this.postEdit(val).then(() => {
        this.center = [this.record.lat, this.record.lng];
        this.marker.position = {
          lat: this.record.lat,
          lng: this.record.lng,
        };
        setTimeout(function () {
          window.dispatchEvent(new Event("resize"));
        }, 250);
      });
      this.setForm({
        add: true,
        edit: true,
      });
    },
    postUpdateRecord: function () {
      this.postUpdate(this.record).then(() => {
        this.closeForm();
      });
    },
    postDeleteRecord: function (val) {
      this.postConfirmDelete(val);
    },
    postDownload(val) {
      window.open(val, "__blank");
    },
    uploadFile: function () {
      this.assignFileBrowse({
        fileType: [".pdf"],
        query: {
          doctype: "documents",
        },
        callback: (response) => {
          setTimeout(() => {
            this.filename = response.name;
            this.record.filename = response.name;
          }, 500);
        },
      });
    },
    centerUpdated(center) {
      this.center = center;
    },
    getUserPosition: async function () {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition((pos) => {
          this.center = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          this.center = [pos.coords.latitude, pos.coords.longitude];
          this.marker.position = {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
          };
        });
      }
    },
  },
  watch: {
    "marker.position": {
      handler() {
        this.record.lat = this.marker.position.lat;
        this.record.lng = this.marker.position.lng;
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.area {
  min-height: 100%;
  background: #0a1224;
}

.area-panel {
  background: #101b36;
  border: 1px solid #1e2c4f;
  border-radius: 12px;
  overflow: hidden;
}

.area-panel-header {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #1e2c4f;
}

.area-panel-title {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.8px;
  color: #e2e8f0;
}

.area-panel-count {
  margin-left: 10px;
  padding: 2px 10px;
  border-radius: 999px;
  background: #0a1224;
  border: 1px solid #1e2c4f;
  font-size: 10px;
  font-weight: 600;
  color: #93c5fd;
}

.area-search >>> .v-input__slot {
  border: 1px solid #1e2c4f;
  border-radius: 8px;
}

.area-btn-add {
  border-radius: 8px;
  font-weight: 700;
  letter-spacing: 0.5px;
  background: linear-gradient(135deg, #1d4ed8 0%, #0b3a79 100%) !important;
}

/* tabel gelap */
.area-table {
  background: #101b36 !important;
}

.area-table >>> thead th {
  background: #0d1730 !important;
  color: #93c5fd !important;
  font-size: 11px !important;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #1e2c4f !important;
}

.area-table >>> tbody td {
  color: #cbd5e1 !important;
  font-size: 13px !important;
  border-bottom: 1px solid #16224384 !important;
}

.area-table >>> tbody tr:hover {
  background: #16224384 !important;
}

.area-name {
  display: flex;
  align-items: center;
  font-weight: 600;
  color: #e2e8f0;
}

/* dialog */
.area-dialog {
  border: 1px solid #1e2c4f;
}

.area-dialog-header {
  display: flex;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid #1e2c4f;
  background: #0d1730;
}

.area-dialog-divider {
  border-color: #1e2c4f !important;
}
</style>
